/**
 * Legacy adapter over the canonical admin-1 registry.
 *
 * The registry (`shared/src/region/admin1-registry.ts`) is the source of truth
 * for subregions and is COUNTRY-SCOPED. Everything here exists to keep the
 * pre-existing call sites byte-identical while they migrate:
 *
 *  - the country-less helpers keep their merged-map precedence (Australia is
 *    spread last, so 'WA' is Western Australia and 'NT' is Northern Territory);
 *  - the five shipped storefront countries keep the FROZEN legacy tables,
 *    because the registry keys France and the UK by ISO 3166-2 code (IDF, SCT)
 *    where these tables key them by name, and that key is what is persisted in
 *    `UserLocation.region` and sent to the AI today.
 *
 * The other 19 countries have no legacy table and are served from the registry.
 */

import {
  LEGACY_AUSTRALIAN_REGIONS,
  LEGACY_CANADIAN_PRUID_REGIONS,
  LEGACY_CANADIAN_REGIONS,
  LEGACY_FRENCH_REGIONS,
  LEGACY_UK_REGIONS,
  LEGACY_US_REGIONS,
  RegionMapping,
} from 'shared/src/region/legacy-admin1-tables';
import {
  findSubregionCode,
  getSubregionName,
  getSubregionsForCountry,
} from 'shared/src/region/admin1-registry';

export type { RegionMapping };

// Canadian provinces - supports both 2-letter codes and PRUID codes
const CANADIAN_REGIONS: Record<string, RegionMapping> = {
  ...LEGACY_CANADIAN_REGIONS,
  ...LEGACY_CANADIAN_PRUID_REGIONS,
};

// Spread order is the precedence: Australia last, so 'WA' resolves to Western
// Australia and 'NT' to Northern Territory. This is pinned by a test — changing
// it silently rewrites the region context sent to the AI.
const ALL_REGIONS: Record<string, RegionMapping> = {
  ...CANADIAN_REGIONS,
  ...LEGACY_US_REGIONS,
  ...LEGACY_FRENCH_REGIONS,
  ...LEGACY_UK_REGIONS,
  ...LEGACY_AUSTRALIAN_REGIONS,
};

/**
 * Convert a region code to its full name with country
 * @param regionCode - The region code (e.g., 'CA10', 'QC', 'NY', etc.)
 * @returns Object with region name and country, or null if not found
 *
 * ⚠ NOT FOR PROMPTS — see `formatRegionForAI` below. Only two UI call sites
 * remain, neither of which formats copy from the result.
 *
 * @deprecated Country-less lookup: collisions resolve by spread precedence, not
 * by fact. Use `getRegionNameForCountry(country, region)` from
 * `shared/src/region/country-region-name`, or `getSubregion(country, raw)` from
 * `shared/src/region/admin1-registry`, when the country is known.
 */
export function getRegionFullName(regionCode: string): RegionMapping | null {
  if (!regionCode || regionCode === 'All regions') return null;

  // Direct lookup
  if (ALL_REGIONS[regionCode]) {
    return ALL_REGIONS[regionCode];
  }

  // Case-insensitive fallback
  const regionCodeUpper = regionCode.toUpperCase();
  for (const [key, value] of Object.entries(ALL_REGIONS)) {
    if (key.toUpperCase() === regionCodeUpper) {
      return value;
    }
  }

  return null;
}

/**
 * Format region for AI context message
 * @param regionCode - The region code
 * @param languageCode - Language code for localization (en, fr, es, de)
 * @returns Formatted string like "Quebec, Canada" or null if not found
 *
 * ⚠ NOT FOR PROMPTS. Merged-map precedence, so ES "MD" formats as "Maryland,
 * United States" and US "WA" as "Western Australia, Australia". Every prompt
 * path has been moved to `formatRegionForCountry(country, region)` from
 * `shared/src/region/country-region-name`, and
 * `frontend/__tests__/region-prompt-country-scope.test.ts` fails if one comes
 * back. Kept, unchanged, so the legacy strings stay available and pinned.
 *
 * @deprecated Country-less lookup, and `languageCode` has never been honoured.
 * Use `formatRegionForCountry(country, region)`, or
 * `formatSubregionForAI(country, code, lang)` from
 * `shared/src/region/admin1-registry` when localization is wanted.
 */
export function formatRegionForAI(regionCode: string, languageCode: string = 'en'): string | null {
  const region = getRegionFullName(regionCode);
  if (!region) return null;

  return `${region.name}, ${region.country}`;
}

/**
 * Reverse-lookup: find a region code from a region name string (e.g. from ipinfo.io).
 * ipinfo returns full region names like "Quebec", "Ontario", "California", etc.
 * This function finds the matching 2-letter code for a given country.
 *
 * @param regionName - The region name from ipinfo (e.g., "Quebec", "California")
 * @param countryCode - The country code to search within (e.g., "CA", "US", "FR", "UK", "AU")
 * @returns The 2-letter region code (e.g., "QC", "NY") or null if not found
 */
export function findRegionCodeByName(regionName: string, countryCode: string): string | null {
  if (!regionName || !countryCode) return null;

  const regionMap = getRegionMapForCountry(countryCode);
  if (!regionMap) {
    // No frozen table: the registry covers the remaining 19 countries and also
    // resolves aliases, local names and legacy codes.
    return findSubregionCode(countryCode, regionName);
  }

  const normalizedInput = regionName.toLowerCase().trim();

  // Search through the region map for a name match (only 2-letter / short codes, skip PRUID codes like CA01)
  for (const [code, mapping] of Object.entries(regionMap)) {
    // Skip PRUID-style codes (e.g., CA01, CA02) — we only want the canonical short codes
    if (/^[A-Z]{2}\d{2}$/.test(code)) continue;

    if (mapping.name.toLowerCase() === normalizedInput) {
      return code;
    }
  }

  // Fuzzy fallback: check if the input partially matches (e.g., "Québec" vs "Quebec")
  for (const [code, mapping] of Object.entries(regionMap)) {
    if (/^[A-Z]{2}\d{2}$/.test(code)) continue;

    // Normalize accents for comparison
    const normalizedName = mapping.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const normalizedQuery = normalizedInput.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

    if (normalizedName === normalizedQuery) {
      return code;
    }
  }

  return null;
}

/**
 * Get the frozen legacy table for one of the five shipped storefront countries,
 * or null when the registry should answer instead.
 */
function getRegionMapForCountry(countryCode: string): Record<string, RegionMapping> | null {
  switch (countryCode) {
    case 'CA': return CANADIAN_REGIONS;
    case 'US': return LEGACY_US_REGIONS;
    case 'FR': return LEGACY_FRENCH_REGIONS;
    case 'UK': return LEGACY_UK_REGIONS;
    case 'AU': return LEGACY_AUSTRALIAN_REGIONS;
    default: return null;
  }
}

/**
 * Get all regions for a country as an array of { code, name } pairs.
 * Returns only canonical short codes (2-3 letter codes), not PRUID or full-name duplicates.
 * Useful for rendering region chips in the UI.
 *
 * @param countryCode - The country code (e.g., "CA", "US")
 * @returns Array of { code, name } objects sorted alphabetically by name
 */
export function getRegionsForCountry(countryCode: string): Array<{ code: string; name: string }> {
  const regionMap = getRegionMapForCountry(countryCode);
  if (!regionMap) {
    // Registry-backed: already sorted by name, and previously these countries
    // rendered no chips at all.
    return getSubregionsForCountry(countryCode).map(({ code, name }) => ({ code, name }));
  }

  const seen = new Set<string>();
  const results: Array<{ code: string; name: string }> = [];

  for (const [code, mapping] of Object.entries(regionMap)) {
    // Skip PRUID-style codes (e.g., CA01)
    if (/^[A-Z]{2}\d{2}$/.test(code)) continue;
    // Skip full-name entries that duplicate short codes (e.g., "New South Wales" when "NSW" exists)
    if (seen.has(mapping.name)) continue;

    seen.add(mapping.name);
    results.push({ code, name: mapping.name });
  }

  return results.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Display strings the inline table in search-bar.tsx disagreed with Natural
 * Earth about. Pinned so the region indication copy stays byte-identical.
 * Canada ships en-CA and fr-CA only, so 'QC:en' is the sole reachable one; the
 * two German entries pin the dead columns in case a de-CA locale is ever added.
 */
const PINNED_CA_LABELS: Record<string, string> = {
  'QC:en': 'Québec',
  'QC:de': 'Quebec',
  'PE:de': 'Prinz-Edward-Insel',
};

/**
 * Localized province/state name for UI copy (the region indication message).
 *
 * Parity note: the table this replaced only localized Canada and rendered the
 * bare code for every other country. That is preserved — widening it is a copy
 * change, not a refactor.
 */
export function getLocalizedRegionName(
  countryCode: string,
  regionCode: string,
  languageCode: string,
): string {
  if (countryCode !== 'CA') return regionCode;

  const pinned = PINNED_CA_LABELS[`${regionCode}:${languageCode}`];
  if (pinned) return pinned;

  return getSubregionName(countryCode, regionCode, languageCode) ?? regionCode;
}
